<script setup lang="ts">
import {
	computed,
	nextTick,
	onBeforeUnmount,
	ref,
	watch
} from "vue";
import { jobsStore } from "@/store/jobs";
import JobsPanelListItem from "@/components/main/jobs-panel/JobsPanelListItem.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppScrollTopButton from "@/components/ui-kit/button/AppScrollTopButton.vue";
import { useScrollTopButton } from "@/composables/useScrollTopButton";

const store = jobsStore()

const jobs = computed(() => store.allJobs)
const isLoadingMore = computed(() => store.isLoadingMore)

const bottomSentinelIndex = computed(() => {
	if (jobs.value.length === 0) return -1

	return Math.max(0, jobs.value.length - 5)
})

const topSentinelIndex = computed(() => {
	if (jobs.value.length === 0) return -1

	return Math.min(4, jobs.value.length - 1)
})

const $container = ref<HTMLElement | null>(null)
const isAutoScrolling = ref(false)

const { isScrollTopEnabled, scrollToTop } = useScrollTopButton(
	$container,
	'[data-job-id]',
	() => jobs.value
)

let bottomObserver: IntersectionObserver | null = null
let topObserver: IntersectionObserver | null = null
let currentBottomEl: Element | null = null
let currentTopEl: Element | null = null

const loadAbovePreservingScroll = async () => {
	if (!$container.value) return

	const prevHeight = $container.value.scrollHeight
	const prevTop = $container.value.scrollTop
	await store.loadMoreJobsAbove()
	await nextTick()
	$container.value.scrollTop = prevTop + ($container.value.scrollHeight - prevHeight)
}

const bindBottomSentinel = (el: Element | null) => {
	if (el === currentBottomEl) return

	if (bottomObserver) {
		bottomObserver.disconnect()
		bottomObserver = null
	}
	currentBottomEl = el
	if (el && $container.value) {
		bottomObserver = new IntersectionObserver((entries) => {
			if (isAutoScrolling.value) return
			if (entries[0]?.isIntersecting && store.hasMoreBelow) {
				store.loadMoreJobsBelow()
			}
		}, {
			root: $container.value,
			threshold: 0,
		})
		bottomObserver.observe(el)
	}
}

const bindTopSentinel = (el: Element | null) => {
	if (el === currentTopEl) return

	if (topObserver) {
		topObserver.disconnect()
		topObserver = null
	}
	currentTopEl = el
	if (el && $container.value) {
		topObserver = new IntersectionObserver((entries) => {
			if (isAutoScrolling.value) return
			if (entries[0]?.isIntersecting && store.hasMoreAbove) {
				loadAbovePreservingScroll()
			}
		}, {
			root: $container.value,
			threshold: 0,
		})
		topObserver.observe(el)
	}
}

watch(() => store.jobsLoading, (newVal) => {
	if (newVal && !store.highlightedJobId) {
		$container.value?.scrollTo({ top: 0 })
	}
})

watch(() => store.highlightedJobId, async (id) => {
	if (!id) return

	await nextTick()
	const el = $container.value?.querySelector<HTMLElement>(`[data-job-id="${id}"]`)
	if (!el) return

	isAutoScrolling.value = true
	el.scrollIntoView({ block: 'center', behavior: 'smooth' })
	setTimeout(() => { isAutoScrolling.value = false }, 700)

	el.classList.add('jobs-panel-list-item--highlight')
	const onEnd = () => {
		el.classList.remove('jobs-panel-list-item--highlight')
		el.removeEventListener('animationend', onEnd)
		store.clearHighlightedJob()
	}
	el.addEventListener('animationend', onEnd)
}, { immediate: true })

onBeforeUnmount(() => {
	bottomObserver?.disconnect()
	topObserver?.disconnect()
	bottomObserver = null
	topObserver = null
	currentBottomEl = null
	currentTopEl = null
})
</script>

<template>
	<div ref="$container" class="jobs-panel-list">
		<template
			v-for="(job, index) in jobs"
			:key="job.Id"
		>
			<div
				v-if="index === topSentinelIndex"
				:ref="(el: Element | null) => bindTopSentinel(el)"
				class="jobs-panel-list__sentinel"
				aria-hidden="true"
			/>
			<jobs-panel-list-item :job="job"/>
			<div
				v-if="index + 1 < jobs.length"
				class="jobs-panel-list__divider"
			/>
			<div
				v-if="index === bottomSentinelIndex"
				:ref="(el: Element | null) => bindBottomSentinel(el)"
				class="jobs-panel-list__sentinel"
				aria-hidden="true"
			/>
		</template>
		<div
			v-if="isLoadingMore"
			class="jobs-panel-list__loader-more"
		>
			<app-loader />
		</div>
		<app-scroll-top-button
			:enabled="isScrollTopEnabled"
			@on-click="scrollToTop"
		/>
	</div>
</template>

<style scoped lang="scss">
.jobs-panel-list {
	display: flex;
	flex-direction: column;
	position: relative;
	padding: 8px 0 8px 8px;
	overflow: auto;

	&__divider {
		flex-shrink: 0;
		width: 100%;
		height: 2px;
		margin: 12px 0;
		border-radius: 1px;
		background-color: #F2F2F2;
	}

	&__sentinel {
		width: 100%;
		height: 1px;
		pointer-events: none;
	}

	&__loader-more {
		display: flex;
		position: relative;
		width: 100%;
		min-height: 60px;
	}
}
</style>
